import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import { formatApiError } from "../utils/errorFormatter";
import { useDomain } from "../contexts/DomainContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Shield, Mail, Lock, ArrowRight, Loader2, ShieldCheck, KeyRound, CheckCircle, FileCheck, Users } from "lucide-react";
import { PublicPageCopy } from "../components/PublicPageCopy";
import axios from "axios";
import LanguageSelector from "../components/LanguageSelector";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tempToken, setTempToken] = useState(null);
  const [companyBranding, setCompanyBranding] = useState(null);
  const { login, requires2FA } = useAuth();
  const { domainInfo, isCustomDomain, branding: domainBranding } = useDomain();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const from = location.state?.from?.pathname || "/app/my-day";
  const companySlug = searchParams.get("company");

  // Redirect if already logged in
  const { token: authToken, user: authUser } = useAuth();
  const skipAutoRedirect = useRef(false);
  useEffect(() => {
    if (authToken && !skipAutoRedirect.current) {
      navigate(authUser?.mfa_setup_required ? '/mfa-setup' : (authUser?.role === 'network_admin' ? '/app/dashboard' : '/app/my-day'), { replace: true });
    }
  }, [authToken, authUser, navigate]);

  // Fetch company branding - use domain context for custom domains, or query param
  useEffect(() => {
    const fetchBranding = async () => {
      // If on custom domain, use domain context branding
      if (isCustomDomain && domainInfo?.found) {
        setCompanyBranding({
          branding: domainInfo.branding,
          slug: domainInfo.slug
        });
        return;
      }
      
      // Otherwise use company slug from URL
      if (companySlug) {
        try {
          const response = await axios.get(`${API}/company/public/${companySlug}`);
          if (response.data && response.data.id !== "default") {
            setCompanyBranding(response.data);
          }
        } catch (error) {
          console.error("Failed to fetch company branding:", error);
        }
      }
    };
    fetchBranding();
  }, [companySlug, isCustomDomain, domainInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (show2FA && !totpCode) {
      toast.error("Please enter your 2FA code");
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password, show2FA ? totpCode : null);

      // login() never throws — it always returns {success: true|false, ...}
      // Failure case must explicitly toast.error and bail BEFORE the success branch
      // (that's what was causing the "Welcome back" loop — failed logins were
      // hitting the success branch because we relied on try/catch).
      if (!result?.success) {
        toast.error(result?.error || "Login failed — please check your credentials");
        return;
      }

      if (result?.requires2FA) {
        setShow2FA(true);
        toast.info("Please enter your 2FA code");
      } else if (result?.requiresMfaSetup) {
        toast.info("One more step: turn on two-step sign-in");
        navigate("/mfa-setup", { replace: true });
      } else if (result?.must_change_password) {
        // User has temporary password - must change it
        setTempToken(result.token);
        setShowChangePassword(true);
        toast.info("Please change your temporary password to continue");
      } else if (!result?.user) {
        // Defensive: success=true but no user payload — never navigate away
        toast.error("Login response missing user data — please try again");
      } else if (result?.backupCodeUsed) {
        skipAutoRedirect.current = true;
        toast.info("You used a backup code. Set up your authenticator on this phone now.");
        navigate("/mfa-setup?replace=1", { replace: true });
      } else {
        toast.success("Welcome back!");
        navigate(from, { replace: true });
      }
    } catch (error) {
      toast.error(formatApiError(error, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  // Handle password change for temporary passwords
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: password,
          new_password: newPassword
        },
        {
          headers: { Authorization: `Bearer ${tempToken}` }
        }
      );
      
      toast.success("Password changed successfully! Please log in again.");
      
      // Reset state
      setShowChangePassword(false);
      setTempToken(null);
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
    } catch (error) {
      toast.error(formatApiError(error, "Failed to change password"));
    } finally {
      setLoading(false);
    }
  };

  // Dynamic branding values
  const brandName = companyBranding?.branding?.company_name || "HospiceGuard";
  const brandTagline = companyBranding?.branding?.tagline || "Audit Defense System";
  const primaryColor = companyBranding?.branding?.primary_color || "#10b981";

  return (
    <>
    <div className="min-h-screen flex">
      {/* Left side - Futuristic Defense Hero */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #020617 0%, #0f172a 40%, #1e3a5f 100%)" }}
      >
        {/* Animated cyber grid pattern */}
        <div className="absolute inset-0 cyber-grid opacity-30" />
        
        {/* Animated particles background */}
        <div className="particles-bg absolute inset-0" />
        
        {/* Glowing orbs with animation */}
        <div 
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full blur-[100px] opacity-30 animate-float"
          style={{ backgroundColor: primaryColor }}
        />
        <div 
          className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full blur-[80px] opacity-20 animate-float"
          style={{ backgroundColor: "#06b6d4", animationDelay: "2s" }}
        />
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[120px] opacity-10"
          style={{ backgroundColor: "#8b5cf6" }}
        />

        <div className="relative z-10 flex flex-col justify-center p-12 lg:p-16">
          {/* Logo with glow effect */}
          <div className="flex items-center gap-4 mb-14">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-2xl relative overflow-hidden"
              style={{ 
                backgroundColor: primaryColor,
                boxShadow: `0 0 40px -5px ${primaryColor}80`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
              {companyBranding?.branding?.logo_url ? (
                <img src={companyBranding.branding.logo_url} alt={brandName} className="w-full h-full object-contain rounded-2xl relative z-10" />
              ) : (
                <Shield className="w-8 h-8 text-white relative z-10" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight font-manrope">{brandName}</h1>
              <p className="text-sm text-slate-400">{brandTagline}</p>
            </div>
          </div>
          
          <h2 className="text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight font-manrope">
            Protect Your<br />
            <span className="text-gradient-primary"
              style={{ 
                background: `linear-gradient(135deg, ${primaryColor}, #06b6d4)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent"
              }}
            >Compliance</span>
          </h2>
          <p className="text-lg text-slate-400 mb-12 max-w-md leading-relaxed">
            AI-powered audit defense system with LCD compliance scoring, eligibility assessments, and HIPAA-compliant documentation.
          </p>

          {/* Feature list with enhanced styling */}
          <div className="space-y-6">
            <div className="flex items-center gap-4 group hover-card-futuristic p-3 -m-3 rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30 shadow-glow-sm group-hover:shadow-glow transition-all duration-300">
                <ShieldCheck className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-semibold">HIPAA Compliant</p>
                <p className="text-sm text-slate-400">End-to-end encryption & audit logging</p>
              </div>
            </div>
            <div className="flex items-center gap-4 group hover-card-futuristic p-3 -m-3 rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30 shadow-glow-cyan group-hover:shadow-glow-cyan transition-all duration-300">
                <FileCheck className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Audit Defense</p>
                <p className="text-sm text-slate-400">AI-generated defense packages</p>
              </div>
            </div>
            <div className="flex items-center gap-4 group hover-card-futuristic p-3 -m-3 rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center border border-violet-500/30 group-hover:shadow-[0_0_30px_-5px_rgba(139,92,246,0.4)] transition-all duration-300">
                <Users className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Multi-Tenant</p>
                <p className="text-sm text-slate-400">Secure multi-organization support</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form with glass effect */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8 bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 relative overflow-hidden">
        {/* Subtle background orbs for light mode */}
        <div className="absolute top-20 right-20 w-64 h-64 rounded-full blur-3xl opacity-30 bg-emerald-200 dark:bg-emerald-900/30" />
        <div className="absolute bottom-20 left-20 w-48 h-48 rounded-full blur-3xl opacity-20 bg-cyan-200 dark:bg-cyan-900/30" />
        
        <div className="w-full max-w-md relative z-10">
          {/* Language selector at top right */}
          <div className="absolute top-4 right-4 fixed">
            <LanguageSelector variant="full" />
          </div>
          
          {/* Mobile logo with glow */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg relative overflow-hidden"
              style={{ 
                backgroundColor: primaryColor,
                boxShadow: `0 0 30px -5px ${primaryColor}60`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
              <Shield className="w-7 h-7 text-white relative z-10" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-manrope">{brandName}</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">{brandTagline}</p>
            </div>
          </div>

          <Card className="border border-white/20 dark:border-slate-700/50 shadow-2xl shadow-slate-200/50 dark:shadow-slate-900/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl overflow-hidden relative">
            {/* Top gradient border */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-cyan-500 to-violet-500" />
            
            <CardHeader className="space-y-1 pb-6 pt-8">
              <CardTitle className="text-2xl font-bold text-slate-900 dark:text-white font-manrope">
                {showChangePassword ? "Change Password" : show2FA ? "Verify Identity" : "Welcome back"}
              </CardTitle>
              <CardDescription className="text-slate-500">
                {showChangePassword
                  ? "Please set a new password to continue"
                  : show2FA 
                    ? "Enter your authenticator code to continue"
                    : "Sign in to access your defense dashboard"}
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-8">
              {showChangePassword ? (
                <form onSubmit={handleChangePassword} className="space-y-5">
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      You&apos;re using a temporary password. Please create a new secure password to continue.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-sm font-semibold">New Password</Label>
                    <div className="relative group">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pl-11 h-12 rounded-xl border-2 border-slate-200 dark:border-slate-700 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 bg-white dark:bg-slate-800/50 transition-all duration-200"
                        data-testid="new-password-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Minimum 8 characters</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-sm font-semibold">Confirm Password</Label>
                    <div className="relative group">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-11 h-12 rounded-xl border-2 border-slate-200 dark:border-slate-700 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 bg-white dark:bg-slate-800/50 transition-all duration-200"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-12 rounded-xl text-base font-semibold bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/40 transition-all duration-300"
                    disabled={loading || !newPassword || !confirmPassword}
                    data-testid="change-password-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Changing Password...
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5 mr-2" />
                        Set New Password
                      </>
                    )}
                  </Button>
                </form>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {!show2FA ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-semibold">{t("auth.email")}</Label>
                      <div className="relative group">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="nurse@hospice.org"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-11 h-12 rounded-xl border-2 border-slate-200 dark:border-slate-700 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 bg-white dark:bg-slate-800/50 transition-all duration-200"
                          data-testid="email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-sm font-semibold">{t("auth.password")}</Label>
                        <Link 
                          to="/forgot-password" 
                          className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
                          data-testid="forgot-password-link"
                        >
                          {t("auth.forgotPassword")}
                        </Link>
                      </div>
                      <div className="relative group">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                        <Input
                          id="password"
                          type="password"
                          placeholder={t("auth.password")}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-11 h-12 rounded-xl border-2 border-slate-200 dark:border-slate-700 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 bg-white dark:bg-slate-800/50 transition-all duration-200"
                          data-testid="password-input"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="totp" className="text-sm font-semibold">{t("settings.twoFactor")}</Label>
                    <div className="relative group">
                      <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                      <Input
                        id="totp"
                        type="text"
                        placeholder="6-digit code or backup code"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value)}
                        className="pl-11 h-14 rounded-xl border-2 border-slate-200 dark:border-slate-700 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 bg-white dark:bg-slate-800/50 text-center text-2xl tracking-[0.5em] font-mono transition-all duration-200"
                        maxLength={8}
                        data-testid="totp-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Enter the code from Google Authenticator or your backup code
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl font-bold text-white shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl relative overflow-hidden group"
                  style={{ 
                    background: `linear-gradient(135deg, ${primaryColor}, #06b6d4)`,
                    boxShadow: `0 0 30px -5px ${primaryColor}60`
                  }}
                  disabled={loading}
                  data-testid="login-button"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  {loading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <ArrowRight className="w-5 h-5 mr-2" />
                  )}
                  {loading ? t("common.loading") : show2FA ? t("common.confirm") : t("auth.signIn")}
                </Button>

                {show2FA && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full h-11 rounded-xl"
                    onClick={() => {
                      setShow2FA(false);
                      setTotpCode("");
                    }}
                  >
                    {t("common.back")}
                  </Button>
                )}
              </form>
              )}

              {!show2FA && !showChangePassword && (
                <div className="mt-8 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {t("auth.signUp")}?{" "}
                    <Link to="/register" className="text-emerald-600 dark:text-emerald-400 hover:underline font-semibold" data-testid="register-link">
                      {t("auth.register")}
                    </Link>
                  </p>
                  <div
                    className="text-xs text-muted-foreground border-t border-slate-200 dark:border-slate-700 pt-3"
                    data-testid="login-partnership-cta"
                  >
                    <p className="mb-1 font-medium text-slate-600 dark:text-slate-300">
                      Consultant or partnership inquiry?
                    </p>
                    <a
                      href="mailto:nsarpong@goodwinliving.org?subject=HospiceGuard%20Partnership%20Inquiry"
                      className="text-emerald-600 dark:text-emerald-400 hover:underline"
                      data-testid="login-partnership-email"
                    >
                      nsarpong@goodwinliving.org
                    </a>
                    {" · "}
                    <a
                      href="tel:+15713296603"
                      className="text-emerald-600 dark:text-emerald-400 hover:underline"
                      data-testid="login-partnership-phone"
                    >
                      (571) 329-6603
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
    <PublicPageCopy page="login" withSeo />
    </>
  );
};

export default LoginPage;
